/**
 * Single source of truth for the per-request chat capability tiers — the ordered set that renders the
 * client model selector AND the ceiling used to enforce a per-org maximum tier server-side. Shared so
 * server (`server/utils/models.ts`, `server/utils/chatTierAccess.ts`, `resolveAdkChatModel`) and client
 * (`useModelSelector`, the admin org page) all agree on the tier ids, their order, labels, and default.
 *
 * ORDER CASES: `CHAT_TIERS` is ascending capability/cost. An index into it is a tier's rank, and the
 * per-org `max_chat_tier` is a ceiling — a requested tier above it is clamped down (`clampChatTier`).
 *
 * The tier *ids* (`fast`/`thinking`/`pro`) are load-bearing: localStorage, the `max_chat_tier` DB
 * column, and the provider tier→model maps in `models.ts`. Do NOT rename them — only the user-facing
 * `CHAT_TIER_LABELS` change (Fast / Balanced / Pro).
 */
export const CHAT_TIERS = ['slow', 'fast', 'thinking', 'pro'] as const
export type ChatTier = (typeof CHAT_TIERS)[number]

/** UI default (matches `useModelSelector`'s initial value). */
export const DEFAULT_CHAT_TIER: ChatTier = 'fast'
/** Highest tier — the default org ceiling, i.e. "no restriction". */
export const MAX_CHAT_TIER: ChatTier = 'pro'

/** User-facing labels. Change these freely; the ids above are contractual. */
export const CHAT_TIER_LABELS: Record<ChatTier, string> = {
  slow: 'Slow',
  fast: 'Fast',
  thinking: 'Balanced',
  pro: 'Pro',
}

export function isChatTier(v: unknown): v is ChatTier {
  return typeof v === 'string' && (CHAT_TIERS as readonly string[]).includes(v)
}

/** Coerce an unknown alias to a known tier, defaulting to `DEFAULT_CHAT_TIER`. */
export function normalizeChatTier(requested?: string | null): ChatTier {
  return isChatTier(requested) ? requested : DEFAULT_CHAT_TIER
}

/** Rank (index) of a tier in the ascending capability order. */
export function chatTierRank(tier: ChatTier): number {
  return CHAT_TIERS.indexOf(tier)
}

/**
 * Clamp a requested tier to an org's ceiling: if the request outranks `max`, return `max`. Both inputs
 * are coerced defensively (unknown `max` ⇒ no restriction). Used server-side (enforcement, in
 * `resolveAdkChatModel`) and client-side (selector clamp).
 */
export function clampChatTier(requested?: string | null, max?: string | null): ChatTier {
  const req = normalizeChatTier(requested)
  const ceiling = isChatTier(max) ? max : MAX_CHAT_TIER
  return chatTierRank(req) > chatTierRank(ceiling) ? ceiling : req
}

/** The tiers a given ceiling allows, in display order. Unknown `max` ⇒ all tiers. */
export function allowedChatTiers(max?: string | null): ChatTier[] {
  const ceiling = isChatTier(max) ? max : MAX_CHAT_TIER
  return CHAT_TIERS.filter(t => chatTierRank(t) <= chatTierRank(ceiling))
}
